import { isBookingProcessAlias } from '../transactions/transaction';
import { getListingImage, isArrayLength } from './genericHelpers';
import { createResourceLocatorString } from './routes';
import { createSlug } from './urlHelpers';
import { types as sdkTypes } from '../util/sdkLoader';
import { uploadImages } from '../containers/EditListingPage/EditListingPage.duck';
const { Money } = sdkTypes;

const DEFAULT_LISTING_TYPE_DATA = {
  listingType: 'sell-new-products',
  transactionProcessAlias: 'default-purchase/release-1',
  unitType: 'item',
};

const resolveListingTypeDefaults = listingTypes => {
  if (Array.isArray(listingTypes) && listingTypes.length > 0) {
    const matchingDefault = listingTypes.find(
      conf => conf?.listingType === DEFAULT_LISTING_TYPE_DATA.listingType
    );
    if (matchingDefault?.transactionType) {
      const { alias, unitType } = matchingDefault.transactionType;
      return {
        listingType: matchingDefault.listingType,
        transactionProcessAlias: alias,
        unitType,
      };
    }

    if (listingTypes.length === 1 && listingTypes[0]?.transactionType) {
      const { listingType, transactionType } = listingTypes[0];
      return {
        listingType,
        transactionProcessAlias: transactionType.alias,
        unitType: transactionType.unitType,
      };
    }
  }

  return { ...DEFAULT_LISTING_TYPE_DATA };
};

export const resolveListingTypeData = (listingTypes, current = {}) => {
  const defaults = resolveListingTypeDefaults(listingTypes);
  return {
    listingType: current?.listingType || defaults.listingType,
    transactionProcessAlias:
      current?.transactionProcessAlias || defaults.transactionProcessAlias,
    unitType: current?.unitType || defaults.unitType,
  };
};

export const ensureListingTypeData = (publicData = {}, listingTypes) => {
  const resolved = resolveListingTypeData(listingTypes, publicData || {});
  return {
    ...publicData,
    listingType: publicData?.listingType || resolved.listingType,
    transactionProcessAlias:
      publicData?.transactionProcessAlias || resolved.transactionProcessAlias,
    unitType: publicData?.unitType || resolved.unitType,
  };
};

// Helper to remove an image from the list
export const handleRemoveImage = (index, images, setImages) => {
  const updatedImages = [...images];
  updatedImages.splice(index, 1);
  setImages(updatedImages);
};

// Function to upload images and return uploaded images
export const handleImageUploads = async ({ images, dispatch, listingImageConfig }) => {
  const imageUploads = [...(images || [])];
  const uploadedImages = await dispatch(uploadImages(imageUploads, listingImageConfig));

  return { images: uploadedImages };
};
// Helper function to navigate to listing or manage listings page
export const navigateToListingPage = ({
  listingId,
  listingTitle,
  history,
  routeConfiguration,
  isDraft,
}) => {
  const targetPage = 'ManageListingsPage';

  const path = createResourceLocatorString(targetPage, routeConfiguration, {});
  // setTimeout(() => {
  history.push(path);
  // }, 1000);
};

export const getTransactionInfo = (
  listingTypes,
  existingListingTypeInfo = {},
  inlcudeLabel = false
) => {
  const { listingType, transactionProcessAlias, unitType } = existingListingTypeInfo;

  if (listingType && transactionProcessAlias && unitType) {
    return { listingType, transactionProcessAlias, unitType };
  } else if (listingTypes.length === 1) {
    const { listingType: type, label, transactionType } = listingTypes[0];
    const { alias, unitType: configUnitType } = transactionType;
    const labelMaybe = inlcudeLabel ? { label: label || type } : {};
    return {
      listingType: type,
      transactionProcessAlias: alias,
      unitType: configUnitType,
      ...labelMaybe,
    };
  }
  return {};
};

export const hasSetListingType = publicData => {
  const { listingType, transactionProcessAlias, unitType } = publicData;
  const existingListingTypeInfo = { listingType, transactionProcessAlias, unitType };

  return {
    hasExistingListingType: !!listingType && !!transactionProcessAlias && !!unitType,
    existingListingTypeInfo,
  };
};

export const setNoAvailabilityForUnbookableListings = processAlias => {
  return isBookingProcessAlias(processAlias)
    ? {}
    : {
        availabilityPlan: {
          type: 'availability-plan/time',
          timezone: 'Etc/UTC',
          entries: [
            // Note: "no entries" is the same as seats=0 for every entry.
            // { dayOfWeek: 'mon', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'tue', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'wed', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'thu', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'fri', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'sat', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'sun', startTime: '00:00', endTime: '00:00', seats: 0 },
          ],
        },
      };
};

export const getInitialValues = (props, existingListingTypeInfo, listingTypes) => {
  const { listing = {}, config } = props || {};
  const currency = config?.currency;
  const { attributes = {}, images = [], currentStock } = listing || {};
  const { description, title, price, publicData, privateData } = attributes || {};
  const { discountApplicable = false } = publicData || {};

  const {
    listingType,
    deliveryOptions,
    shippingPriceInSubunitsAdditionalItems,
    shippingPriceInSubunitsOneItem,
    condition,
    packaging,
    unitOfMeasurement,
    teamName,
    ...rest
  } = publicData;
  const normalizedPackaging = Array.isArray(packaging) ? packaging[0] : packaging;
  const photos = getListingImage(images);
  // Initial values for the form
  return {
    ...rest,
    images: photos,
    title,
    description,
    unitOfMeasurement,
    packaging: normalizedPackaging,
    deliveryOptions: ['shipping'],
    price,
    condition,
    gender:publicData?.gender,
    brand: publicData.brand ?? '',
    techSuitTier: publicData.techSuitTier ?? '',
    material: publicData.material ?? '',
    color: publicData.color ?? '',
    teamName,
    stock: currentStock?.attributes?.quantity || 1,
    shippingPriceInSubunitsAdditionalItems: new Money(
      shippingPriceInSubunitsAdditionalItems ?? 0,
      currency
    ),
    shippingPriceInSubunitsOneItem: new Money(shippingPriceInSubunitsOneItem ?? 0, currency),
    // Transaction type info: listingType, transactionProcessAlias, unitType
    ...getTransactionInfo(listingTypes, existingListingTypeInfo),
  };
};
// Helper function to handle image uploads and combining existing + new images
export const handleImages = async ({ images, listingImageConfig, dispatch }) => {
  const existingImages = images.filter(photo => photo.id);
  const newImages = images.filter(photo => !photo.id)?.map(({ file }) => file);
  let uploadedImages = [];

  if (isArrayLength(newImages)) {
    const { images } = await handleImageUploads({
      images: newImages,
      listingImageConfig,
      dispatch,
    });

    if (isArrayLength(images)) {
      uploadedImages = images; // Newly uploaded images
    }
  }

  // Combine existing images with newly uploaded ones
  return [...existingImages, ...uploadedImages];
};
